import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';
import AppDataGrid from '../common/AppDataGrid';
import GridActionsCell from '../common/GridActionsCell';
import TableSearch from '../common/TableSearch';
import { useDataTable } from '../../hooks/useDataTable';

const StoresList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperAdmin } = useStore();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);

  // Page, size, search and sort live in the URL (/stores?page=0&size=20&search=...).
  const table = useDataTable(storeService.getStoresPage, {}, {
    onError: () => toast.error(t('stores.fetchError')),
  });
  const fetchStores = table.refresh;

  const handleDelete = async () => {
    try {
      await storeService.deleteStore(storeToDelete.id);
      toast.success(t('stores.deleteSuccess'));
      fetchStores();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const columns = [
    {
      field: 'logo',
      headerName: '',
      width: 60,
      sortable: false,
      cellRenderer: (params) => (
        params.data.logoUrl ? (
          <Avatar src={params.data.logoUrl} alt={params.data.name} sx={{ my: 0.5 }} />
        ) : (
          <Avatar sx={{ bgcolor: 'primary.main', my: 0.5 }}>
            <StoreIcon />
          </Avatar>
        )
      )
    },
    { field: 'id', headerName: t('common.id'), width: 70 },
    { field: 'name', headerName: t('stores.storeName'), width: 200 },
    {
      field: 'code',
      headerName: t('stores.storeCode'),
      width: 150,
      cellRenderer: (params) => (
        <Chip label={params.value} size="small" color="primary" />
      )
    },
    { field: 'email', headerName: t('common.email'), width: 200 },
    { field: 'phone', headerName: t('common.phone'), width: 150 },
    { field: 'address', headerName: t('common.address'), flex: 1 },
    {
      field: 'userCount',
      headerName: t('users.title'),
      width: 100,
      cellRenderer: (params) => (
        <Chip label={params.value || 0} color="info" size="small" />
      )
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 120,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.active') : t('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: ViewIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/stores/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/stores/edit/${params.data.id}`),
            },
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              hidden: !isSuperAdmin,
              onClick: () => {
                setStoreToDelete(params.data);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('stores.title')}</Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/stores/create')}
          >
            {t('stores.createStore')}
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 2, p: 2 }}>
        <TableSearch
          placeholder={t('common.search')}
          value={table.searchInput}
          onChange={table.handleSearchChange}
        />
      </Card>

      <Card>
        <AppDataGrid
          rows={table.data.content}
          columns={columns}
          loading={table.loading}
          paginationMode="server"
          rowCount={table.data.totalElements}
          paginationModel={table.paginationModel}
          onPaginationModelChange={table.handlePaginationModelChange}
          sortModel={table.sortModel}
          onSortModelChange={table.handleSortChange}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('stores.deleteStore')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('stores.deleteConfirm', { name: storeToDelete?.name })}
            {storeToDelete?.userCount > 0 && (
              <strong> {t('stores_ext.storeHasUsers', { count: storeToDelete.userCount })}</strong>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('stores.deleteStore')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoresList;
